import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of, EMPTY } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import {
  IrisApplicationsActionsTypes,
  IrisApplicationsActions,
  LoadApplications,
  LoadApplicationsComplete, SetApplication, SetApplicationComplete, SetAppAlias,
} from '@iris/common/redux/actions/applications-actions';
import { IrisApplicationsService } from '@iris/common/services/utils/iris-applications.service';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { ROUTER_REQUEST } from '@ngrx/router-store';
import { select, Store } from '@ngrx/store';
import { selectRouteParam } from '../router.selectors';
import { AuthFacade } from '@iris/modules/auth/utils/auth.facade';

@Injectable()
export class IrisApplicationsEffects {
  private readonly actions$ = inject(Actions);
  private readonly globalSandbox = inject(IrisGlobalSandbox);
  private readonly irisApplicationsService = inject(IrisApplicationsService);
  private readonly store = inject(Store);
  private readonly authFacade = inject(AuthFacade);

  loadApplications$: Observable<IrisApplicationsActions> = createEffect(() => this.actions$.pipe(
    ofType<LoadApplications>(IrisApplicationsActionsTypes.LoadApplications),
    switchMap(() => this.irisApplicationsService.getApplicationList().pipe(map(applications => new LoadApplicationsComplete(applications)))),
  ));

  updateCurrentApplicationOnLoadApplicationsComplete$ = createEffect(() => this.actions$.pipe(
    ofType<LoadApplicationsComplete>(IrisApplicationsActionsTypes.LoadApplicationsComplete),
    switchMap(() => {
      const appAlias = this.globalSandbox.appAlias();
      if (appAlias) {
        return of(new SetApplication(appAlias));
      }
      return EMPTY;
    }),
  ));

  setCurrentApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SetApplication>(IrisApplicationsActionsTypes.SetApplication),
      withLatestFrom(this.globalSandbox.applications$),
      switchMap(([action, applications]) => {
        const targetAlias = action.payload;
        const targetApplication = applications.find(
          (application) => application.applicationAlias === targetAlias,
        );

        if (targetApplication) {
          return of(
            new SetAppAlias(targetAlias),
            new SetApplicationComplete(targetApplication),
          );
        }

        return EMPTY;
      }),
    ),
  );

  handleAppAliasRouteParam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_REQUEST),
      filter(() => this.authFacade.isLoggedIn()),
      switchMap(() => this.store.pipe(select(selectRouteParam('appAlias')))),
      distinctUntilChanged(),
      filter(appAlias => appAlias !== this.globalSandbox.appAlias()),
      map((appAlias) => {
        return new SetApplication(appAlias);
      }),
    ),
  );
}
